import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IconDashesHorizontal } from "@/components/_icons/IconDashesHorizontal";
import IconHeartbeatCircle from "@/components/_icons/IconHeartbeatCircle";
import { TruncatedText } from "@/components/_molecules/truncated-text/truncated-text";
import { cn } from "@/lib/cn";

import { Status } from "../models/status-model";
import { displayNameForStatusType, ensureAccessibleStatusColor, StatusList, StatusType } from "../models/status-types";

interface StatusViewProps {
  status?: Status | null;
  type?: StatusType | null;
  modified?: boolean;
  logoOnly?: boolean;
  canTruncateLabel?: boolean;
  className?: string;
}

export const StatusView: FC<StatusViewProps> = ({ status, type, modified, canTruncateLabel = false, ...props }) => {
  const { t } = useTranslation("common");
  const statusType = type;

  const color = useMemo(() => {
    if (status?.color) {
      return ensureAccessibleStatusColor(status.color);
    }
    if (!statusType) {
      return "#666a73";
    }
    if (statusType === StatusList.onTrack) {
      return "#1a7f37";
    }
    if (statusType === StatusList.atRisk) {
      return "#9a6700";
    }
    if (statusType === StatusList.offTrack) {
      return "#cf222e";
    }
    if (statusType === StatusList.blocked) {
      return "#44484F";
    }
  }, [status, statusType]);

  const displayName = useMemo(() => {
    if (status?.name) {
      return status.name;
    }
    if (statusType) {
      return displayNameForStatusType(statusType);
    }
    return t("notAssigned");
  }, [status, statusType, t]);

  return (
    <div
      className={cn(["flex flex-row items-center gap-2", canTruncateLabel && "min-w-0"])}
      style={{ color }}
      data-testid="status-view"
    >
      {!statusType && !status && (
        <IconDashesHorizontal className={cn(["h-4 w-4 shrink-0", props.className])} style={{ color }} />
      )}
      {(statusType || status) && (
        <IconHeartbeatCircle className={cn(["h-4 w-4 shrink-0", props.className])} style={{ color }} />
      )}
      {!props.logoOnly && (
        <>
          {canTruncateLabel ? (
            <TruncatedText text={displayName} />
          ) : (
            <span className="whitespace-normal">{displayName}</span>
          )}
          {modified && (
            <div className="ml-1 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }}></div>
          )}
        </>
      )}
    </div>
  );
};
